import { useCallback } from 'react'
import { useAuth } from '@clerk/react'
import { api, baseURL } from '../lib/api'

export function useAuthedApi() {
  const { getToken, isLoaded, isSignedIn } = useAuth()

  const authHeaders = useCallback(async () => {
    if (!isLoaded) {
      throw new Error('Authentication is still loading')
    }
    if (!isSignedIn) {
      throw new Error('User is not signed in')
    }

    let token = await getToken()
    if (!token && typeof window !== 'undefined') {
      const clerk = (
        window as Window & {
          Clerk?: {
            session?: {
              getToken?: () => Promise<string | null>
            }
          }
        }
      ).Clerk

      token = (await clerk?.session?.getToken?.()) ?? null
    }

    if (!token) {
      throw new Error('Missing authentication token')
    }

    return { Authorization: `Bearer ${token}` }
  }, [getToken, isLoaded, isSignedIn])

  /** JSON GET */
  const get = useCallback(
    async <T>(url: string): Promise<T> => {
      const headers = await authHeaders()
      const res = await api.get<T>(url, { headers })
      return res.data
    },
    [authHeaders],
  )

  /** JSON POST */
  const post = useCallback(
    async <T>(url: string, body?: unknown): Promise<T> => {
      const headers = await authHeaders()
      const res = await api.post<T>(url, body, { headers })
      return res.data
    },
    [authHeaders],
  )

  /** JSON PATCH */
  const patch = useCallback(
    async <T>(url: string, body?: unknown): Promise<T> => {
      const headers = await authHeaders()
      const res = await api.patch<T>(url, body, { headers })
      return res.data
    },
    [authHeaders],
  )

  /**
   * Multipart POST using fetch — avoids axios Content-Type/boundary conflicts
   * when uploading FormData. Only adds the Authorization header; the browser
   * sets the correct multipart boundary automatically.
   */
  const postForm = useCallback(
    async <T>(url: string, formData: FormData): Promise<T> => {
      const token = await getToken()
      const res = await fetch(`${baseURL}${url}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(body?.error ?? `Upload failed (${res.status})`)
      }
      return res.json() as Promise<T>
    },
    [getToken],
  )

  /** JSON PUT */
  const put = useCallback(
    async <T>(url: string, body?: unknown): Promise<T> => {
      const headers = await authHeaders()
      const res = await api.put<T>(url, body, { headers })
      return res.data
    },
    [authHeaders],
  )

  /** JSON DELETE */
  const del = useCallback(
    async <T>(url: string): Promise<T> => {
      const headers = await authHeaders()
      const res = await api.delete<T>(url, { headers })
      return res.data
    },
    [authHeaders],
  )

  return { get, post, put, patch, del, delete: del, postForm }
}
